/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

import { useEffect, useRef } from "react";

/**
 * Calls `onOutside` when a pointer-down happens outside `elementRef`, or when
 * Escape is pressed. Used to dismiss dropdowns/popovers.
 *
 * This is deliberately listener-based rather than relying on a full-screen
 * backdrop element: a backdrop only catches outside clicks if its z-index sits
 * above every other element, which is fragile next to sticky headers and modals.
 * A document-level listener closes the menu regardless of stacking context.
 *
 * @param {{ current: HTMLElement|null }} elementRef - the popover/menu root.
 * @param {() => void} onOutside - invoked on an outside click or Escape.
 * @param {boolean} active - only listen while true (e.g. while the menu is open).
 */
export function useClickOutside(elementRef, onOutside, active = true) {
  const cb = useRef(onOutside);
  cb.current = onOutside;

  useEffect(() => {
    if (!active) return undefined;
    const handlePointer = (e) => {
      const el = elementRef.current;
      if (el && !el.contains(e.target)) cb.current();
    };
    const handleKey = (e) => {
      if (e.key === "Escape") cb.current();
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [elementRef, active]);
}
